import React, { useMemo, useCallback } from 'react'
import { Table, TableBody, TableCell, TableHead, TableRow, TableContainer } from '@material-ui/core'
import Pagination from '@material-ui/lab/Pagination'
import styled from '@emotion/styled'
import { CSS } from '@dnd-kit/utilities'
import { CSSProperties } from '@material-ui/core/styles/withStyles'
import { get } from 'lodash'
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import {DragIndicator} from '@material-ui/icons'


export type DataSourceProps = any
export type ColumnsProps = {
  title: string
  dataIndex: string
  minWidth?: string | number
  maxWidth?: string | number
  width?: string | number
  align?: 'left' | 'right' | 'inherit' | 'center' | 'justify'
  render?: (data: string, record: any) => JSX.Element
}[]
export type PaginationProps = {
  currentPage: number
  itemCount: number
  itemsPerPage: number
  totalItems: number
  totalPages: number
}
export type TableProps = {
  data: DataSourceProps
  columns: ColumnsProps
  pagination?: PaginationProps
  onDragEnd?: (event: DragEndEvent) => void
  onPageChange?: (page: number) => void
  style?: CSSProperties
  showPaginate?: boolean
}

const DraggableRow = ({ row, children }: { row: any; children: React.ReactNode }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: row.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes}>
      <TableCell
        {...listeners}
        style={{ cursor: 'grab', width: 40 }}
      >
        <DragIndicator />
      </TableCell>
      {children}
    </TableRow>
  )
}


const CustomTableHeader = ({ columns }: { columns: ColumnsProps }) => (
  <TableHead>
    <TableRow>
      <TableCell style={{ width: 40 }} /> {/* This is for the drag icon */}
      {columns.map((column, colIndex) => (
        <TableCell
          key={`${colIndex}-${column.dataIndex}`}
          align={column.align}
          style={{
            minWidth: column.minWidth,
            maxWidth: column.maxWidth,
            width: column.width
          }}
        >
          <h6>{column.title}</h6>
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
)


const CustomTableBody = ({
  columns,
  data,
  onDragEnd
}: {
  columns: ColumnsProps
  data: any[]
  onDragEnd?: (event: DragEndEvent) => void
}) => {
  const renderBody = useMemo(() => {
    return data.map((item, rowIndex) => {
      const cells = columns.map((column, colIndex) => {
        const columnData = get(item, column.dataIndex)
        return (
          <TableCell key={`${rowIndex}-${colIndex}-${column.dataIndex}`}>
            {column.render ? column.render(columnData, item) : columnData}
          </TableCell>
        )
      })

      const rowProps = { key: `row-${rowIndex}`, children: cells }

      return onDragEnd ? (
        <DraggableRow key={`row-${rowIndex}`} row={item}>
          {cells}
        </DraggableRow>
      ) : (
        <TableRow {...rowProps} />
      )
    })
  }, [data, columns, onDragEnd])

  return <TableBody>{renderBody}</TableBody>
}

const RootLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const TableDnd = ({
  data = [],
  columns,
  pagination = {
    currentPage: 0, totalPages: 0,
    itemCount: 0,
    itemsPerPage: 0,
    totalItems: 0
  },
  onPageChange,
  style,
  onDragEnd,
  showPaginate = true
}: TableProps) => {
  const { currentPage, totalPages } = pagination
  const sensors = useSensors(useSensor(PointerSensor))

  const handleChangePage = useCallback(
    (event: unknown, newPage: number) => {
      onPageChange?.(newPage)
    },
    [onPageChange]
  )

  const content = (
    <Table>
      <CustomTableHeader columns={columns} />
      <CustomTableBody columns={columns} data={data} onDragEnd={onDragEnd} />
    </Table>
  )

  return (
    <RootLayout style={style}>
      <TableContainer>
        {onDragEnd ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={data.map((d: { id: string }) => d.id)} strategy={verticalListSortingStrategy}>
              {content}
            </SortableContext>
          </DndContext>
        ) : (
          content
        )}
      </TableContainer>

      {showPaginate && (
        <Pagination
          style={{ marginTop: 10 }}
          count={totalPages || 0}
          color="primary"
          variant="outlined"
          shape="rounded"
          onChange={handleChangePage}
          page={currentPage || 0}
          showFirstButton
          showLastButton
        />
      )}
      
    </RootLayout>
  )
}

export default TableDnd
